import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { AutoSaveIndicator } from '@plato/ui-legacy/components/common/auto-save-indicator';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { Input } from '@plato/ui-legacy/components/ui/input';
import { Page } from '@plato/ui-legacy/components/ui/page';
import { PageHeader } from '@plato/ui-legacy/components/ui/page-header';
import { useTrackedFieldChanges } from '@plato/ui-legacy/lib/hooks/use-tracked-field-changes';
import { captureException } from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import type React from 'react';
import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounceCallback } from 'usehooks-ts';
import { PageTitle } from '../../../components/page-title.tsx';
import type { SalesDraftDefaultHeaderInput } from '../../../server/types/drafts-types.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { draftHeaderFields } from '../../analytics/types/analytics-events.ts';
import { DraftSummary } from '../components/draft-summary.tsx';
import { AddressInput } from '../form/address-input.tsx';
import { ContactPersonInput } from '../form/contact-person-input.tsx';
import { DeliveryDateInput } from '../form/delivery-date-input.tsx';
import { DocumentTypeInput } from '../form/document-type-input.tsx';
import { Field } from '../form/field.tsx';
import { ShippingTypeInput } from '../form/shipping-type-input.tsx';
import { useConfirmDelete } from '../hooks/use-confirm-delete.ts';
import { useConfirmSubmit } from '../hooks/use-confirm-submit.ts';
import { useDraftContext } from '../hooks/use-draft-context.ts';
import { useDraftForm } from '../hooks/use-draft-form.ts';

export function DraftHeaderPage(): React.JSX.Element {
    const { t } = useTranslation('sales-draft');
    const { draftId, draft, isLoading: isDraftLoading } = useDraftContext();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const util = api.useUtils();
    const sendEvent = useAnalytics();

    const {
        canManageTransactionDraftNotes,
        canManageTransactionDraftPoNumber,
        canUpdateTransactionDocumentType,
        canUpdateTransactionShippingType,
        canUpdateTransactionDeliveryDate,
        canUpdateTransactionDueDate,
        transactionHeaderExtraRequiredFields,
    } = useAppConfig();

    const isPoNumberRequired = transactionHeaderExtraRequiredFields.includes('po_number');
    const isContactRequired = transactionHeaderExtraRequiredFields.includes('contact_person_id');

    if (!draftId) {
        // we only get here if static routing is wrong
        throw new Error(`expected route parameter draft_id`);
    }

    const { mutateAsync: getDefaults, isPending: isDefaultsLoading } = useMutation({
        mutationFn: async (values: SalesDraftDefaultHeaderInput) => {
            return await util.platoUser.query.salesDraft.getSalesDraft.getDefaultHeader.fetch(values);
        },
    });

    const { confirmDelete } = useConfirmDelete();
    const { submit, isPending: isSubmitting, error: submitError } = useConfirmSubmit({ draft });

    const { mutate: updateHeader, isPending: isUpdating } =
        api.platoUser.command.salesDraft.draft.updateHeader.useMutation();

    const updateHeaderDebounced = useDebounceCallback(updateHeader, 1000);
    const isLoading = isDraftLoading || isDefaultsLoading || isSubmitting || isUpdating;

    const {
        control,
        getValues,
        handleSubmit,
        register,
        formState: { errors },
        reset,
        subscribe,
        watch,
    } = useDraftForm({
        disabled: isDraftLoading,
    });

    useEffect(
        () =>
            subscribe({
                callback: ({ values }) => {
                    if (!values.draft_order_id) {
                        return;
                    }
                    updateHeaderDebounced(values);
                },
                formState: { values: true },
            }),
        [subscribe, updateHeaderDebounced],
    );

    useTrackedFieldChanges({
        subscribe,
        fields: draftHeaderFields,
        onFieldChange: (field) => {
            sendEvent('draft_header_field_changed', {
                draft_id: getValues('draft_order_id'),
                field,
            });
        },
    });

    const customerId = watch('customer_id');
    const transactionType = watch('transaction_type');

    useEffect(() => {
        if (!customerId) {
            return;
        }

        void (async () => {
            try {
                const defaults = await getDefaults({
                    customer_id: customerId,
                    sales_order_type: transactionType,
                });
                const current = getValues();
                const billing_address_id = defaults.invoice_address.address_id ?? '';
                const shipping_address_id = defaults.shipping_address.address_id ?? '';

                reset({
                    ...current,
                    billing_address_id: current.billing_address_id || billing_address_id,
                    delivery_date: current.delivery_date || defaults.desired_delivery_date,
                    document_type: current.document_type || defaults.sales_order_type,
                    due_date: current.due_date || defaults.due_date,
                    shipping_address_id: current.shipping_address_id || shipping_address_id,
                    shipping_type_id: current.shipping_type_id || defaults.shipping_type,
                });
            } catch (error) {
                captureException(error);
            }
        })();
    }, [customerId, getDefaults, getValues, reset, transactionType]);

    useEffect(() => {
        if (draft && draft.state !== 'NEW') {
            void navigate('/drafts', { replace: true });
        }
    }, [draft, navigate]);

    useEffect(() => {
        if (!draft) {
            return;
        }

        const documentType =
            typeof draft.external?.document_type === 'string' ? draft.external.document_type : undefined;
        const poNumber = typeof draft.external?.po_number === 'string' ? draft.external.po_number : undefined;

        reset({
            billing_address_id: draft.billing_address?.address_id ?? '',
            contact_person_id: draft.contact_person_id ?? '',
            customer_id: draft.customer_id,
            delivery_date: draft.delivery_date ?? '',
            document_type: documentType ?? '',
            draft_order_id: draft.draft_order_id,
            due_date: draft.due_date ?? '',
            notes: draft.notes ?? '',
            po_number: poNumber ?? '',
            shipping_address_id: draft.shipping_address?.address_id ?? '',
            shipping_type_id: draft.shipping_type_id ?? '',
            transaction_type: draft.type,
        });
    }, [draft, reset]);

    function handleBack(): void {
        // When the user reached this step in-app (via "Continue"), pop the history entry so the
        // browser back button behaves naturally afterwards and we don't leave a duplicate
        // line-items entry behind. `location.key` is only 'default' when the header URL was opened
        // directly — in that case there's nothing to pop, so navigate to the line-items step.
        if (location.key === 'default') {
            void navigate(
                {
                    pathname: '../line-items',
                    search: searchParams.toString(),
                },
                {
                    replace: true,
                },
            );
        } else {
            void navigate(-1);
        }
    }

    return (
        <Page className="max-h-dvh overflow-hidden">
            <PageTitle
                title={
                    transactionType === 'QUOTE'
                        ? t('sales_draft__header__sub_title__quote')
                        : t('sales_draft__header__sub_title__order')
                }
            />
            <PageHeader
                title={
                    transactionType === 'QUOTE'
                        ? t('sales_draft__header__sub_title__quote')
                        : t('sales_draft__header__sub_title__order')
                }
            >
                <AutoSaveIndicator isUpdating={isLoading} />
            </PageHeader>
            <form
                className="flex grow min-h-0 flex-col gap-4 overflow-hidden sm:flex-row"
                onSubmit={handleSubmit(submit)}
            >
                <div className="flex grow min-h-0 flex-col gap-4 overflow-y-auto">
                    <div className="flex flex-auto flex-col gap-2.5">
                        <div className="flex flex-col gap-3.5">
                            <Field
                                error={errors.billing_address_id}
                                label={t('sales_draft__header__billing_address')}
                                required
                            >
                                <Controller
                                    control={control}
                                    name="billing_address_id"
                                    render={({ field, formState }) => (
                                        <AddressInput
                                            customerId={draft?.customer_id}
                                            disabled={formState.disabled}
                                            name="billing_address_id"
                                            onChange={field.onChange}
                                            placeholder={t('sales_draft__header__shipping_address')}
                                            value={field.value}
                                        />
                                    )}
                                />
                            </Field>

                            <Field
                                error={errors.shipping_address_id}
                                label={t('sales_draft__header__shipping_address')}
                                required
                            >
                                <Controller
                                    control={control}
                                    name="shipping_address_id"
                                    render={({ field, formState }) => (
                                        <AddressInput
                                            customerId={draft?.customer_id}
                                            disabled={formState.disabled}
                                            name="shipping_address_id"
                                            onChange={field.onChange}
                                            placeholder={t('sales_draft__header__shipping_address')}
                                            value={field.value}
                                        />
                                    )}
                                />
                            </Field>

                            <Field
                                error={errors.contact_person_id}
                                label={t('sales_draft__header__contact_person')}
                                required={isContactRequired}
                            >
                                <Controller
                                    control={control}
                                    name="contact_person_id"
                                    render={({ field, formState }) => (
                                        <ContactPersonInput
                                            customerId={draft?.customer_id}
                                            disabled={formState.disabled}
                                            onChange={field.onChange}
                                            placeholder={t('sales_draft__header__contact_person__select')}
                                            value={field.value}
                                        />
                                    )}
                                />
                            </Field>

                            {transactionType === 'ORDER' ? (
                                <Field
                                    label={t('sales_draft__header__delivery_date')}
                                    error={errors.delivery_date}
                                    required
                                >
                                    <Controller
                                        control={control}
                                        name="delivery_date"
                                        render={({ field }) => (
                                            <DeliveryDateInput
                                                label={t('sales_draft__header__delivery_date')}
                                                onChange={field.onChange}
                                                readonly={!canUpdateTransactionDeliveryDate}
                                                value={field.value}
                                            />
                                        )}
                                    />
                                </Field>
                            ) : (
                                <Field
                                    label={t('sales_draft__header__due_date')}
                                    error={errors.due_date}
                                    required
                                >
                                    <Controller
                                        control={control}
                                        name="due_date"
                                        render={({ field }) => (
                                            <DeliveryDateInput
                                                label={t('sales_draft__header__due_date')}
                                                onChange={field.onChange}
                                                readonly={!canUpdateTransactionDueDate}
                                                value={field.value}
                                            />
                                        )}
                                    />
                                </Field>
                            )}

                            {canUpdateTransactionDocumentType && (
                                <Field
                                    label={
                                        transactionType === 'ORDER'
                                            ? t('sales_draft__header__order_type')
                                            : t('sales_draft__header__quote_type')
                                    }
                                    required
                                >
                                    <Controller
                                        control={control}
                                        name="document_type"
                                        render={({ field, formState }) => (
                                            <DocumentTypeInput
                                                customerId={draft?.customer_id}
                                                disabled={formState.disabled}
                                                onChange={field.onChange}
                                                placeholder={
                                                    transactionType === 'ORDER'
                                                        ? t('sales_draft__header__order_type__select')
                                                        : t('sales_draft__header__quote_type__select')
                                                }
                                                storageId={draft?.storage_id}
                                                transactionType={transactionType}
                                                value={field.value}
                                            />
                                        )}
                                    />
                                </Field>
                            )}

                            {canUpdateTransactionShippingType && (
                                <Field
                                    error={errors.shipping_type_id}
                                    label={t('sales_draft__header__shipping_type')}
                                    required={isContactRequired}
                                >
                                    <Controller
                                        control={control}
                                        name="shipping_type_id"
                                        render={({ field, formState }) => (
                                            <ShippingTypeInput
                                                onChange={field.onChange}
                                                disabled={formState.disabled}
                                                placeholder={t('sales_draft__header__shipping_type__select')}
                                                value={field.value}
                                            />
                                        )}
                                    />
                                </Field>
                            )}

                            {canManageTransactionDraftPoNumber && (
                                <Field
                                    error={errors.po_number}
                                    label={t('sales_draft__header__commission')}
                                    required={isPoNumberRequired}
                                >
                                    <Input {...register('po_number')} />
                                </Field>
                            )}

                            {canManageTransactionDraftNotes && (
                                <Field error={errors.notes} label={t('sales_draft__header__notes')}>
                                    <Input
                                        placeholder={t('sales_draft__header__notes_placeholder')}
                                        {...register('notes')}
                                    />
                                </Field>
                            )}
                        </div>
                        {submitError && (
                            <div className="text-danger text-sm">
                                {submitError.data?.displayMessage ?? t('sales_draft__header__submit_error')}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex flex-col gap-4 sm:w-80 shrink-0">
                    {draft ? <DraftSummary draft={draft} /> : <div className="grow" />}

                    <div className="flex flex-col gap-4">
                        <Button variant="primary" type="submit" disabled={isUpdating}>
                            {t('sales_draft__button_group__submit')}
                        </Button>
                        <div className="flex items-stretch gap-3 *:grow">
                            <Button variant="subtle" onClick={handleBack}>
                                {t('sales_draft__button_group__back_to_line_items')}
                            </Button>
                            <Button
                                variant="subtle"
                                onClick={() => {
                                    confirmDelete(draftId);
                                }}
                            >
                                {t('sales_draft__button_group__cancel')}
                            </Button>
                        </div>
                    </div>
                </div>
            </form>
        </Page>
    );
}
